import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, EDIT } from "const"
import {
    RrsTemplate,
    RrsTemplateCreate,
} from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    RrsTemplateValidation,

    SaveRrsTemplate,
    GetMaxRrsTemplates,
    IsLockedRrsTemplate,

    setRrsTemplateEdit,
    setRrsTemplateReloadList,
    setRrsTemplateReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    RadioButton,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditTemplateProps {
    template: RrsTemplate
}

let isMounted: boolean

const Form = ({
    template,
}: EditTemplateProps) => {
    const baseRoute = useSelector((state: StoreType) => state.rrsTemplate.baseRoute)
    const templateEdit = useSelector((state: StoreType) => state.rrsTemplate.templateEdit)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(template.name)
    const [isDefault, setIsDefault] = useState(template.default)
    const [locked, setLocked] = useState(template.locked)
    const [singleRiskMap, setSingleRiskMap] = useState(template.singleRiskMap)

    const [validation, setValidation] = useState<RrsTemplateValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        forceRerenderContent(baseRoute, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        let { riskMapGenerated } = templateEdit // !!! IMPORTANT !!! take value from redux state
        if (template.singleRiskMap !== singleRiskMap) riskMapGenerated = false

        const newTemplateData: RrsTemplateCreate = {
            accountId: template.accountId || userData.accountId,
            projectId: template.projectId || project.id,
            name,
            default: isDefault,
            locked,
            singleRiskMap,
            riskMapGenerated,
        }

        const { maxTemplates } = GetMaxRrsTemplates(packageSettings, userData)
        SaveRrsTemplate(
            template.id,
            newTemplateData,
            maxTemplates,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newTemplateId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newTemplateId}`)
            }
            if (res.validation) return

            dispatch(setRrsTemplateEdit({
                id: res.newTemplateId || template.id,
                ...newTemplateData,
            }))

            dispatch(setRrsTemplateReloadList(false))
            setTimeout(() => {
                dispatch(setRrsTemplateReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setRrsTemplateReloadEdit(false))
                setTimeout(() => {
                    dispatch(setRrsTemplateReloadEdit(true))
                }, 0)
            }
        })
    }

    const isLocked = IsLockedRrsTemplate(project.id, template)

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    {
                        !project.id && (
                            <FormGroup>
                                <Label>{lang.default}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={isDefault}
                                        onChange={() => setIsDefault(true)}
                                        title={lang.yes}
                                        readOnly={isLocked}
                                    />
                                    <RadioButton
                                        checked={!isDefault}
                                        onChange={() => setIsDefault(false)}
                                        title={lang.no}
                                        readOnly={isLocked}
                                    />
                                </FormGroup>
                            </FormGroup>
                        )
                    }
                    {
                        !project.id && userData.accountId !== 1 && (
                            <FormGroup>
                                <Label>{lang.locked}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={locked}
                                        onChange={() => setLocked(true)}
                                        title={lang.yes}
                                        readOnly={isLocked}
                                    />
                                    <RadioButton
                                        checked={!locked}
                                        onChange={() => setLocked(false)}
                                        title={lang.no}
                                        readOnly={isLocked}
                                    />
                                </FormGroup>
                            </FormGroup>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.singleRiskMap}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={singleRiskMap}
                                onChange={() => setSingleRiskMap(true)}
                                title={lang.yes}
                                readOnly={isLocked}
                            />
                            <RadioButton
                                checked={!singleRiskMap}
                                onChange={() => setSingleRiskMap(false)}
                                title={lang.no}
                                readOnly={isLocked}
                            />
                        </FormGroup>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        onClick={() => onSave(1)}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button
                        variant="success"
                        row
                        onClick={() => onSave(2)}
                    >
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
